import { buildUrlPath } from "lib/resolveUrl";
import { authFetch, readResponseErrorDetail } from "./base";
export const fetchAllEvents = async () => {
    const response = await authFetch('api/events/');
    if (!response.ok) {
        throw new Error(`Failed to fetch events: ${await readResponseErrorDetail(response)}`);
    }
    const payload = await response.json();
    return payload.data;
};
export const fetchEvent = async (eventId) => {
    const response = await authFetch(`api/events/${eventId}`);
    if (!response.ok) {
        if (response.status === 404) {
            return 'not-found';
        }
        throw new Error(`Failed to fetch event: ${await readResponseErrorDetail(response)}`);
    }
    const payload = await response.json();
    return payload.data;
};
export const fetchPublicEventById = async (eventId) => {
    const response = await fetch(buildUrlPath(`/api/public/events/${eventId}`));
    if (!response.ok) {
        if (response.status === 404) {
            return 'not-found';
        }
        throw new Error(`Failed to fetch event: ${await readResponseErrorDetail(response)}`);
    }
    const payload = await response.json();
    return payload.data;
};
export const createEvent = async (title, description, customId) => {
    const trimmedId = customId?.trim();
    const body = { title, description };
    if (trimmedId) {
        body.id = trimmedId;
    }
    const response = await authFetch('api/events/', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
    });
    if (!response.ok) {
        if (response.status === 409) {
            return 'already-exists';
        }
        if (response.status === 400) {
            return 'invalid-id-format';
        }
        throw new Error(`Failed to insert event: ${await readResponseErrorDetail(response)}`);
    }
    const json = await response.json();
    return json.data;
};
export const updateEventId = async (eventId, newId) => {
    const response = await authFetch(`api/events/${eventId}`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: newId }),
    });
    if (!response.ok) {
        if (response.status === 409) {
            return 'already-exists';
        }
        throw new Error(`Failed to update event: ${await readResponseErrorDetail(response)}`);
    }
    const json = await response.json();
    return json.data;
};
export const fetchEventUserlex = async (eventId) => {
    const response = await authFetch(`api/events/${eventId}/userlex`);
    if (!response.ok) {
        throw new Error('Failed to fetch event userlex');
    }
    const payload = await response.json();
    return payload.data.userlex ?? null;
};
export const updateEventUserlex = async (eventId, userlex) => {
    const response = await authFetch(`api/events/${eventId}/userlex`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userlex }),
    });
    if (!response.ok) {
        throw new Error('Failed to update event userlex');
    }
    const payload = await response.json();
    return payload.data.userlex ?? null;
};
export const fetchSegmentTranslationPrompt = async (eventId, segmentId, language) => {
    const response = await authFetch(`api/events/${eventId}/segments/${segmentId}/translation-prompts/${encodeURIComponent(language)}`);
    if (!response.ok) {
        throw new Error('Failed to fetch translation prompt');
    }
    const payload = await response.json();
    return payload.data.customPrompt ?? null;
};
export const updateSegmentTranslationPrompt = async (eventId, segmentId, language, customPrompt) => {
    const response = await authFetch(`api/events/${eventId}/segments/${segmentId}/translation-prompts/${encodeURIComponent(language)}`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ customPrompt }),
    });
    if (!response.ok) {
        throw new Error('Failed to update translation prompt');
    }
    const payload = await response.json();
    return payload.data.customPrompt ?? null;
};
export const upsertTextReplacement = async (eventId, oldText, newText) => {
    await authFetch(`api/events/${eventId}/replacements`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ oldText, newText }),
    });
};
export const deleteTextReplacement = async (eventId, oldText) => {
    await authFetch(`api/events/${eventId}/replacements/${encodeURIComponent(oldText)}`, {
        method: 'DELETE',
    });
};
export const createPresenter = async (eventId, name) => {
    await authFetch(`api/events/${eventId}/presenters`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name }),
    });
};
export const updatePresenter = async (eventId, presenterId, patch) => {
    await authFetch(`api/events/${eventId}/presenters/${encodeURIComponent(presenterId)}`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(patch),
    });
};
export const deletePresenter = async (eventId, presenterId) => {
    await authFetch(`api/events/${eventId}/presenters/${encodeURIComponent(presenterId)}`, {
        method: 'DELETE',
    });
};
export const updateCreateBeeyProject = async (eventId, createBeeyProject) => {
    await authFetch(`api/events/${eventId}/beey-settings`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ createBeeyProject }),
    });
};
export const deleteBeeyProject = async (eventId, segmentId) => {
    const response = await authFetch(`api/events/${eventId}/segments/${segmentId}/beey-project`, {
        method: 'DELETE',
    });
    if (!response.ok) {
        throw new Error(`Failed to delete Beey project: ${await readResponseErrorDetail(response)}`);
    }
};
export const deleteEvent = async (eventId) => {
    const response = await authFetch(`api/events/${eventId}`, {
        method: 'DELETE',
    });
    if (!response.ok) {
        throw new Error(`Failed to delete event: ${await readResponseErrorDetail(response)}`);
    }
};
export const deleteSegment = async (eventId, segmentId) => {
    const response = await authFetch(`api/events/${eventId}/segments/${segmentId}`, {
        method: 'DELETE',
    });
    if (!response.ok) {
        throw new Error(`Failed to delete segment: ${await readResponseErrorDetail(response)}`);
    }
};
export const downloadSegmentRecording = async (eventId, segmentId, audioLanguage) => {
    const response = await authFetch(`api/events/${eventId}/segments/${segmentId}/recording`);
    if (!response.ok) {
        let detail = response.statusText;
        try {
            const json = (await response.json());
            if (json.message) {
                detail = json.message;
            }
        }
        catch {
            /* ignore */
        }
        throw new Error(detail);
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    try {
        const fileName = `${eventId}+${segmentId}+${audioLanguage}.wav`;
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = fileName;
        anchor.click();
    }
    finally {
        URL.revokeObjectURL(url);
    }
};
